/* eslint-disable @typescript-eslint/no-explicit-any */
import {AfterViewInit, Component, DestroyRef, DoCheck, ElementRef, Input, NgZone, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {OverlayModule} from '@angular/cdk/overlay';
import {CommonModule} from '@angular/common';
import {FormsModule, NgForm} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {ColumnMode, DatatableComponent, NgxDatatableModule, SelectionType, SortType} from '@siemens/ngx-datatable';
import {FeatureViewerItemLookup} from '../../../../../../common/models/feature-viewer-item.model';
import {MapToolsService} from '../../../../../services/map-tools.service';
import {AttributeViewerService} from '../../../attribute-viewer.service';
import {AttributeViewerColumnService} from '../../../attribute-viewer-column.service';
import {AttributeViewerFilterService} from '../../../attribute-viewer-filter.service';
import {AttributeViewerEditorService} from '../../../attribute-viewer-editor.service';
import {SpatialUtilService} from '../../../../../services/spatial-util.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {PlanModelService} from '../../../../../../common/service/plan-model.service';
import {auditTime, BehaviorSubject, debounceTime, distinctUntilChanged, map, Observable, Subject, tap} from 'rxjs';
import {AttributeViewerContext} from '../../../attribute-viewer-context.model';
import {NlrHasPermissionsPipe} from '../../../../../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../../../../../common/pipe/nlr-permissions-disabled-pipe';
import {NlrAnglePatternDirective} from '../../../../../../common/directive/nlr-angle-pattern.directive';
import {NlrPatternDirective} from '../../../../../../common/directive/nlr-pattern.directive';
import {MaxLengthInputLimitDirective} from '../../../../../../common/directive/max-length-input-limit.directive';
import {NlrFormFieldHasErrorPipe} from '../../../../../../common/pipe/nlr-form-field-has-error.pipe';
import {NlrTypeheadDirective} from '../../../../../../common/directive/nlr-typehead.directive';
import {WidgetService} from '../../../../../services/widget.service';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../../../../common/constants';
import {HelpLinkComponent} from '../../../../../../help/help-link.component';
import {equals} from '../../../../../../common/service/data-util';
import {filter} from 'rxjs/operators';

@Component({
    selector: 'nlr-feature-table',
    imports: [
        CommonModule,
        FormsModule,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        NlrAnglePatternDirective,
        NlrPatternDirective,
        MaxLengthInputLimitDirective,
        NgxDatatableModule,
        NlrFormFieldHasErrorPipe,
        NlrTypeheadDirective,
        CdkDrag,
        CdkDragHandle,
        OverlayModule,
        HelpLinkComponent,
        NgSelectModule
    ],
    providers: [CommonModule],
    templateUrl: './feature-table.component.html',
    styleUrl: './feature-table.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class FeatureTableComponent implements OnInit, DoCheck, AttributeViewerContext, AfterViewInit {
    @ViewChild('dataTable')
    dataTable: DatatableComponent;

    @Input()
    tool: FeatureViewerItemLookup;

    @Input()
    editorConfig: any;

    @Input()
    isDesignTabActive: boolean;

    @Input()
    disabled: boolean;

    @ViewChild('editorForm')
    editorForm: NgForm;

    protected readonly Object = Object;

    protected readonly ColumnMode = ColumnMode;

    protected readonly SelectionType = SelectionType;

    protected readonly SortType = SortType;

    title = 'FeatureTableComponent';

    featureLayer = {};

    features: any[];

    attributes: any[] = [];

    attrsList: any[] = [];

    private selectedAttrsListAtLastCheck: any[] = [];

    attributeViewerEditorActiveAtLastCheck = false;

    attributesVisibilityAllowed = [];

    editorAttributes = [];

    hideUnselectedRows = false;

    // Filter Features Widget
    showFilter = false;

    filterApplied = false;

    // Column Settings Features Widget
    showColumnSettings = false;

    previousIndex = undefined;

    previousIsDesignTabActive = undefined;

    options = {
        id: 'geometryFeatures',
        previousIndex: this.previousIndex,
        rowHeight: 30,
        headerHeight: 32,
        scrollbarV: true,
        selectable: true,
        multiSelect: true,
        columns: [] as any[],
        columnMode: 'force',
        selectAllCheckboxValue: false,
        messages: {emptyMessage: 'No features found!'}
    };

    sorts: {prop: string; dir: string}[] = [];

    invalidSelectedFeaturesError: string;

    editorActiveAtLastCheck: boolean;

    showColumnSettingsAtLastCheck: boolean;

    showFilterAtLastCheck: boolean;

    initialised: boolean = false;

    constructor(
        private attributeViewerService: AttributeViewerService,
        private attributeViewerColumnService: AttributeViewerColumnService,
        private attributeViewerFilterService: AttributeViewerFilterService,
        private attributeViewerEditorService: AttributeViewerEditorService,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private mapToolsService: MapToolsService,
        private planModelService: PlanModelService,
        private spatialUtilService: SpatialUtilService,
        private widgetService: WidgetService,
        private ngZone: NgZone
    ) {}

    private selectSubject = new Subject<any>();

    onTableSelect(evt: any) {
        this.selectSubject.next(evt);
    }

    ngOnInit() {
        this.attributeViewerEditorService.setContext(this);

        this.selectSubject.subscribe(evt => this.onSelect(evt));

        this.selectedSubject.subscribe(list => this.onSelectedChanged(list));

        this.eventBus
            .on(`moveTo${this.tool.type}FeaturesExtent`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.getTitle()} - on - moveTo${this.tool.type}FeaturesExtent`);
                this.attributeViewerService.moveToFeaturesExtentCenter(
                    {featureLayer: this.featureLayer, selectedAttrsList: this.selectedAttrsList},
                    []
                );
            });

        this.eventBus
            .on(`featuresUpdated`)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                debounceTime(1000),
                tap(() => {
                    console.debug(`${this.getTitle()} - on - featuresUpdated`);

                    this.attributeViewerService.reloadFeatureAttributes(this).subscribe(() => {
                        this.eventBus.cast('geometryFeatures:sortDataTable');
                    });
                })
            )
            .subscribe();

        this.eventBus
            .on(`remove${this.tool.type}Features`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.getTitle()} - on - remove${this.tool.type}Features`);
                const features = meta.data;
                this.attributeViewerService.removeFeatures(this, features);
            });

        this.eventBus
            .on(`deselect${this.tool.type}Features`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.getTitle()} - on - deselect${this.tool.type}Features`);
                const features = meta.data;
                this.attributeViewerService.deselectFeatures(features, this.selectedAttrsList);
            });

        this.eventBus
            .on('attributeViewerResized')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.dataTable.recalculate());

        this.init();
    }

    @ViewChild('dataTable', {read: ElementRef, static: true})
    private dataTableHost!: ElementRef<HTMLElement>;

    ngAfterViewInit(): void {
        //wait until the internal markup exists
        queueMicrotask(() => {
            const viewport = this.dataTableHost.nativeElement.querySelector('.datatable-body') as HTMLElement;
            if (!viewport) {
                return;
            }
            this.ngZone.runOutsideAngular(() => {
                const stop = (e: Event) => e.stopImmediatePropagation();
                // capture=true so the event never reaches ngx-datatable’s own listener
                ['pointerover', 'pointerenter', 'mousemove', 'mouseover', 'mouseenter'].forEach(t => viewport.addEventListener(t, stop, true));
            });
        });
    }

    private readonly selectedSubject = new BehaviorSubject<any[]>([]);

    set selectedAttrsList(v: any[]) {
        this.selectedSubject.next(v);
    }
    get selectedAttrsList(): any[] {
        return this.selectedSubject.value;
    }

    onSelectedChanged(list: any[]) {
        if (!this.initialised) {
            return;
        }

        if (this.isDesignTabActive !== this.previousIsDesignTabActive) {
            this.previousIsDesignTabActive = this.isDesignTabActive;
        }

        if (!equals(list, this.selectedAttrsListAtLastCheck)) {
            console.debug(`${this.getTitle()} - onSelectedChanged - selectedAttrsList - changed`);

            this.attributeViewerService.checkSelectedFeaturesChange(this, list, this.selectedAttrsListAtLastCheck);

            if (this.attributeViewerService.unhighlightFeaturesOutOfContextAllowed()) {
                const featureClass = this.spatialUtilService.getFeatureClass(this.featureLayer);
                const highlightIds = this.spatialUtilService
                    .getHighlightedFeatures(featureClass)
                    .map((f: any) => f.attributes.id)
                    .sort((a: number, b: number) => a - b);
                const selectedAttrIds = list.map(attr => attr.id).sort((a: number, b: number) => a - b);
                if (!equals(selectedAttrIds, highlightIds)) {
                    this.spatialUtilService
                        .getFeatureClasses()
                        .filter(fc => fc !== featureClass)
                        .forEach(fc => this.spatialUtilService.unhighlightFeatures(fc));
                }
            }
            this.selectedAttrsListAtLastCheck = list.slice();
        }
    }

    ngDoCheck() {
        if (!this.initialised) {
            return;
        }
        if (this.mapToolsService.isActive('ATTRIBUTE_EDITOR_WINDOW')) {
            if (!this.attributeViewerEditorActiveAtLastCheck) {
                this.attributeViewerEditorActiveAtLastCheck = true;
                this.attributeViewerEditorService.setupEditor(this).subscribe();
            }
        } else {
            this.attributeViewerEditorActiveAtLastCheck = false;
        }

        if (this.isDesignTabActive !== this.previousIsDesignTabActive) {
            this.previousIsDesignTabActive = this.isDesignTabActive;
        }

        if (this.isEditorActive() && !this.editorActiveAtLastCheck) {
            this.widgetService.setupWidget(`${this.tool.type}AttributeEditor`);
        }
        this.editorActiveAtLastCheck = this.isEditorActive();

        if (this.showColumnSettings && !this.showColumnSettingsAtLastCheck) {
            this.widgetService.setupWidget(`${this.tool.type}ColumnSettingsFeatureWidget`);
        }
        this.showColumnSettingsAtLastCheck = this.showColumnSettings;

        if (this.showFilter && !this.showFilterAtLastCheck) {
            this.widgetService.setupWidget(`${this.tool.type}FilterFeaturesWidget`);
        }
        this.showFilterAtLastCheck = this.showFilter;
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    computeEditorDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        const dragPosition = this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
        this.editorConfig.style = {
            top: dragPosition.y,
            left: dragPosition.x
        };
        return dragPosition;
    };

    getAttributeEditorTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('ATTRIBUTE_EDITOR_WINDOW', 'Attribute Editor');
    }

    isDisabled() {
        return (
            this.mapToolsService.isSuspended('ATTRIBUTE_VIEWER_WINDOW') ||
            this.selectedAttrsList.length === 0 ||
            this.disabled ||
            this.invalidSelectedFeaturesError
        );
    }

    onSelect(evt: {selected: any[]}) {
        console.log(`${this.title} - onSelect - selected ids:`, evt.selected.map(attrs => attrs.id).join());

        // Deselect all rows if evt.selected contains one row that was already selected
        if (
            evt.selected.length === 1 &&
            this.selectedAttrsListAtLastCheck.includes(evt.selected[0]) &&
            !this.attributeViewerService.ctrlKeyDown &&
            !this.attributeViewerService.shiftKeyDown
        ) {
            this.selectedAttrsList = [];
            return;
        }

        if (
            evt.selected.length === 1 &&
            this.selectedAttrsListAtLastCheck.some(r => r.id === evt.selected[0].id) &&
            !this.attributeViewerService.ctrlKeyDown &&
            !this.attributeViewerService.shiftKeyDown
        ) {
            this.selectedAttrsList = [];
            this.dataTable.selected = [];
            return;
        }

        this.selectedAttrsList = [...evt.selected];

        if (this.mapToolsService.isActive('IDENTIFY_FEATURES')) {
            if (this.attributeViewerService.ctrlKeyDown || this.attributeViewerService.shiftKeyDown) {
                this.eventBus.cast('resetFeaturesInteracted');
            } else {
                this.eventBus.cast('closeIdentifiedFeatures');
            }
        }
    }

    onActivate(evt: {event: any; row: any; type: string}) {
        if (evt.type !== 'click') {
            return;
        }
        console.log(`onActivate - row: ${evt.row.id}`);

        if (!(evt.event.ctrlKey || evt.event.shiftKey || this.selectedAttrsList.length === 0)) {
            this.ngZone.runOutsideAngular(() => {
                this.spatialUtilService.unhighlightAllFeatures();

                const attrsListIds = this.attrsList.map(attrs => attrs.id);

                // Filtered out features will not be in vm.attrsList.  Any features selected prior to being filtered out should remain selected
                this.spatialUtilService.highlightFeatures(this.featureLayer, [
                    evt.row.id,
                    ...this.selectedAttrsList.map(attrs => attrs.id).filter(id => !attrsListIds.includes(id))
                ]);
            });
        }
    }

    onSort(evt: {sorts: any[]}) {
        console.log(`${this.title} - onSort - evt:`, evt);
        this.sorts.splice(0, this.sorts.length, ...evt.sorts);
    }

    clearAllRows() {
        this.spatialUtilService.unhighlightFeatures(this.featureLayer);
    }

    editAttributesIsDisabled() {
        return this.planModelService.readonly || this.disabled;
    }

    isReadOnly() {
        return this.planModelService.readonly;
    }

    isEditorActive() {
        return this.mapToolsService.isActive('ATTRIBUTE_EDITOR_WINDOW');
    }

    getAttributesVisibilityAllowed() {
        return this.attributesVisibilityAllowed.sort((a: any, b: any) => a.displayOrder - b.displayOrder);
    }

    setAttributesVisibilityAllowed() {
        this.attributesVisibilityAllowed = [];
        this.attributes.forEach(attribute => {
            if (attribute.hideColumn === true) {
                this.attributesVisibilityAllowed.push(attribute);
            }
        });
    }

    isAttributeRequired(attribute: any) {
        if (this.selectedAttrsList.length === 0) {
            return false;
        }

        if (this.selectedAttrsList.length === 1) {
            return attribute.mandatory || (attribute.prop === 'type' && typeof attribute.value === 'undefined');
        } else if (attribute.bulkUpdate) {
            return false;
        } else {
            return !(
                attribute.attributeType?.includes('DECIMAL') &&
                (Object.hasOwn(this.selectedAttrsList[0], 'x') || Object.hasOwn(this.selectedAttrsList[0], 'y'))
            );
        }
    }

    closeEditor() {
        this.mapToolsService.deactivate('ATTRIBUTE_EDITOR_WINDOW');
        this.editorForm.form.markAsPristine();
    }

    saveEditor() {
        this.attributeViewerEditorService.saveEditor(this, this.editorForm).subscribe();
    }

    getAttributeEditorHelpUrl() {
        const toolTypeKebabCase = this.tool.type
            .split(/(?=[A-Z])/)
            .map(v => v.toLowerCase())
            .join('-');
        return HELP_LINK_CONTENTS_BASE_URL + `attribute-editor-${toolTypeKebabCase}-help.html`;
    }

    getAttributeEditorHelpPageTitle() {
        const toolDescription = {
            angle: 'Angles | Edit',
            controlPoint: 'Control Points | Edit',
            line: 'Lines | Edit',
            point: 'Points | Edit',
            polyline: 'Polylines | Edit',
            polygon: 'Polygons | Edit'
        }[this.tool.type];

        return `Attribute Viewer | ${toolDescription}`;
    }

    getColumnChooserHelpUrl() {
        const toolTypeKebabCase = this.tool.type
            .split(/(?=[A-Z])/)
            .map(v => v.toLowerCase())
            .join('-');
        return HELP_LINK_CONTENTS_BASE_URL + `attribute-viewer-${toolTypeKebabCase}-column-chooser-help.html`;
    }

    getColumnChooserHelpPageTitle() {
        const toolDescription = {
            point: 'Points | Column Chooser',
            line: 'Lines | Column Chooser',
            polygon: 'Polygons | Column Chooser',
            angle: 'Angles | Column Chooser',
            controlPoint: 'Control Points | Column Chooser'
        }[this.tool.type];

        return `Attribute Viewer | ${toolDescription}`;
    }

    getFilterHelpUrl() {
        const toolTypeKebabCase = this.tool.type
            .split(/(?=[A-Z])/)
            .map(v => v.toLowerCase())
            .join('-');
        return HELP_LINK_CONTENTS_BASE_URL + `attribute-viewer-${toolTypeKebabCase}-feature-filter-help.html`;
    }

    getFilterHelpPageTitle() {
        const toolDescription = {
            point: 'Points | Filter Features',
            line: 'Lines | Filter Features',
            polygon: 'Polygons | Filter Features',
            angle: 'Angles | Filter Features',
            controlPoint: 'Control Points | Filter Features'
        }[this.tool.type];

        return `Attribute Viewer | ${toolDescription}`;
    }

    deleteSelectedFeatures() {
        this.attributeViewerService.deleteSelectedFeatures(this);
    }

    hideShowUnselectedRows() {
        this.attributeViewerFilterService.hideShowUnselectedRows(this);
    }

    openEditor() {
        this.attributeViewerService.openEditor(this, true);
    }

    getEditorAttributes() {
        return this.editorAttributes.sort((a: any, b: any) => a.editableOrder - b.editableOrder);
    }

    showOrHideColumn(attribute: any) {
        this.attributeViewerColumnService.showOrHideColumn(this.options, attribute);
    }

    filterFeaturesByAttributeValue(attribute: any, value: string) {
        const features = this.attributeViewerFilterService.filterFeaturesByAttributeValue(this, attribute, value);
        return features.length > 8 ? features.slice(0, 8) : features;
    }

    filterFeaturesSearch(attribute: any) {
        return (text$: Observable<string>) =>
            text$.pipe(
                debounceTime(200),
                distinctUntilChanged(),
                map(term => (term.length === 0 ? [] : this.filterFeaturesByAttributeValue(attribute, term)))
            );
    }

    applyFilter() {
        this.attributeViewerFilterService.applyFilter(this);
    }

    clearFilter() {
        this.attributeViewerFilterService.clearFilter(this);
    }

    attributeSelectorChanged(attribute: any) {
        this.attributeViewerEditorService.attributeSelectorChanged(this, attribute).subscribe();
    }

    onClickColumnSettingsWidget() {
        this.widgetService.moveToTop('attributeViewer');
    }

    onClickFilterFeaturesWidget() {
        this.widgetService.moveToTop('attributeViewer');
    }

    onClickAttributeEditor() {
        this.widgetService.moveToTop('attributeViewer');
    }

    private init() {
        console.debug(`${this.title} - init`);

        switch (this.tool.type) {
            case 'angle':
                this.featureLayer = this.spatialUtilService.getAngleMapLayer();
                break;
            case 'point':
                this.featureLayer = this.spatialUtilService.getPointMapLayer();
                break;
            case 'controlPoint':
                this.featureLayer = this.spatialUtilService.getControlPointMapLayer();
                break;
            case 'line':
                this.featureLayer = this.spatialUtilService.getLineMapLayer();
                break;
            case 'polygon':
                this.featureLayer = this.spatialUtilService.getPolygonMapLayer();
                break;
        }
        window.setTimeout(() => {
            this.attributeViewerColumnService.populateColumns(this, false);
            this.setAttributesVisibilityAllowed();
            this.attributeViewerService
                .reloadFeatureAttributes(this)
                .pipe(
                    tap(() => {
                        if (!this.initialised) {
                            this.selectedAttrsListAtLastCheck = [...this.selectedAttrsList];
                            this.initialised = true;
                        }
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(() => {
                    console.groupCollapsed('[RxJS] reload feature attributes');
                    console.trace();
                    console.groupEnd();
                });
        }, 0);

        this.eventBus
            .on('featureHighlightsChanged')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                auditTime(50),
                filter(() => this.initialised),
                tap(() => console.debug('on featureHighlightChanged feature table')),
                map(() => {
                    const highlightedFeatureIds: any[] = this.spatialUtilService.getHighlightedFeatureIds(this.featureLayer);
                    const selectedAttrsListIds = this.selectedAttrsList.map(attrs => attrs.id);
                    return {highlightedFeatureIds, selectedAttrsListIds};
                }),
                filter(
                    ({highlightedFeatureIds, selectedAttrsListIds}) =>
                        highlightedFeatureIds.length !== selectedAttrsListIds.length ||
                        !selectedAttrsListIds.every(id => highlightedFeatureIds.includes(id))
                ),
                tap(({highlightedFeatureIds, selectedAttrsListIds}) => {
                    if (highlightedFeatureIds.some(id => !selectedAttrsListIds.includes(id)) && !this.mapToolsService.isActive('IDENTIFY_FEATURES')) {
                        this.attributeViewerService.unhighlightFeaturesOutOfContextAllowed(false);
                    }

                    this.selectedAttrsList = this.attrsList.filter(attrs => highlightedFeatureIds.includes(attrs.id));
                })
            )
            .subscribe(); // subscribe is now activation-only

        this.eventBus
            .on(`select${this.tool.type}Features`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.getTitle()} - on - select${this.tool.type}Features`);
                if (!this.initialised) {
                    return;
                }
                const features = meta.data;
                if (!this.mapToolsService.isActive('IDENTIFY_FEATURES')) {
                    this.attributeViewerService.unhighlightFeaturesOutOfContextAllowed(false);
                }
                this.attributeViewerService.selectFeatures(features, this.attrsList, this.selectedAttrsList);
            });
    }

    private getTitle() {
        return `${this.title} - ${this.tool.type}`;
    }
}
